import type { WhatsAppSystemStatusLevel } from '@/services/whatsappSystemStatusService';

/**
 * Severity ordering for the operations status bar.
 *
 * Extracted from WhatsAppSystemStatusBar so it can be tested as behaviour rather than as
 * source text. It lived inline, and the guard written for it asserted the ORDER OF THE
 * KEYS in the rank object — which is cosmetic. Swapping the VALUES, the actual defect,
 * left the key order untouched and the guard passed. Pure logic in its own module and
 * called directly is the only version of that check worth having.
 *
 * `unknown` sits BELOW `degraded`. It originally ranked above (healthy 0, degraded 1,
 * unknown 2, stale 3), so a probe that simply did not answer outranked a confirmed
 * partial fault and drove the whole bar's colour. An unknown is an absence of evidence,
 * not evidence of a fault: escalating it above a measured degradation reports the thing
 * we know least about as the thing most wrong.
 *
 * It still outranks `healthy`, because "one of these did not answer" is worth showing.
 * It just cannot outrank a known problem. Same rule the Instant UCC send pill follows on
 * the Ops Inbox: inconclusive is its own state, never a claim of failure.
 */
export const STATUS_SEVERITY: Record<WhatsAppSystemStatusLevel, number> = {
  healthy: 0,
  unknown: 1,
  degraded: 2,
  stale: 3,
};

export function worstStatus(...levels: WhatsAppSystemStatusLevel[]): WhatsAppSystemStatusLevel {
  return levels.reduce<WhatsAppSystemStatusLevel>(
    (worst, candidate) => (STATUS_SEVERITY[candidate] > STATUS_SEVERITY[worst] ? candidate : worst),
    'healthy',
  );
}
